/**
 * Column names for the CSV mapping step.
 *
 * Excel and Google Sheets wrap every header in quotes. The upload API reads the
 * file with a CSV parser, which removes those quotes, then compares the result
 * to the mapping keys we send. A comma split keeps the quote characters, so a
 * fully quoted header row can never match.
 *
 * Well-formed quoted cells are unwrapped here. A header that is not a clean
 * quoted or plain field keeps the historical comma split, so a malformed row
 * still cannot match a real column name.
 */
export const parseCsvHeaderLine = (headerLine: string): string[] => {
  const parsed = parseWellFormedHeaderLine(headerLine)
  if (parsed !== null) {
    return parsed
  }
  return headerLine.split(',').map(cell => cell.trim())
}

const parseWellFormedHeaderLine = (headerLine: string): string[] | null => {
  const line = headerLine.endsWith('\r') ? headerLine.slice(0, -1) : headerLine
  const headers: string[] = []
  let index = 0

  while (index <= line.length) {
    if (index === line.length) {
      headers.push('')
      break
    }

    if (line[index] === '"') {
      const quoted = readQuotedCell(line, index + 1)
      if (quoted === null) {
        return null
      }
      headers.push(quoted.value)
      index = quoted.nextIndex
    } else {
      const commaIndex = line.indexOf(',', index)
      const end = commaIndex === -1 ? line.length : commaIndex
      const raw = line.slice(index, end)
      if (raw.includes('"')) {
        return null
      }
      headers.push(raw.trim())
      index = end
    }

    if (index === line.length) {
      break
    }
    if (line[index] !== ',') {
      return null
    }
    index += 1
  }

  return headers
}

const readQuotedCell = (
  line: string,
  start: number
): { value: string, nextIndex: number } | null => {
  let value = ''
  let index = start

  while (index < line.length) {
    if (line[index] === '"') {
      if (line[index + 1] === '"') {
        value += '"'
        index += 2
        continue
      }
      return { value, nextIndex: index + 1 }
    }
    value += line[index]
    index += 1
  }

  return null
}
